import { Action, createReducer, on } from '@ngrx/store';
import { BadResponseWithError } from '@shared/models/common.interface';
import * as SeasonActions from '../actions/season.action';
import { Season } from '../../models/seasons-model';

export interface SeasonsState {
    seasons: Season[];
    currentSeason: Season | null;
    levels: any[];
    rewards: any[];
    isLoading: boolean;
    error: BadResponseWithError | null;
}

const initialState: SeasonsState = {
    seasons: [],
    currentSeason: null,
    isLoading: false,
    levels: [],
    rewards: [],
    error: null,
};

const reducer = createReducer(
    initialState,
    // Fetch all seasons
    on(SeasonActions.fetchSeasonsRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.fetchSeasonsSuccess, (state, { payload }) => ({
        ...state,
        seasons: payload || [],
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.fetchSeasonsFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Fetch single season
    on(SeasonActions.fetchSeasonRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.fetchSeasonSuccess, (state, { payload }) => ({
        ...state,
        currentSeason: payload,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.fetchSeasonFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Create season
    on(SeasonActions.createSeasonRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.createSeasonSuccess, (state, { payload }) => ({
        ...state,
        seasons: [...state.seasons, payload],
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.createSeasonFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Update season
    on(SeasonActions.updateSeasonRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.updateSeasonSuccess, (state, { payload }) => ({
        ...state,
        seasons: state.seasons.map((season) =>
            season.id === payload.id ? { ...season, ...payload } : season
        ),
        currentSeason:
            state.currentSeason?.id === payload.id
                ? { ...state.currentSeason, ...payload }
                : state.currentSeason,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.updateSeasonFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Delete season
    on(SeasonActions.deleteSeasonRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.deleteSeasonSuccess, (state, { seasonId }) => ({
        ...state,
        seasons: state.seasons.filter((season) => season.id !== seasonId),
        currentSeason:
            state.currentSeason?.id === seasonId ? null : state.currentSeason,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.deleteSeasonFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Save levels
    on(SeasonActions.saveLevelsRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.saveLevelsSuccess, (state, { payload }) => ({
        ...state,
        levels: payload,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.saveLevelsFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Fetch levels
    on(SeasonActions.fetchLevelsRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.fetchLevelsSuccess, (state, { payload }) => ({
        ...state,
        levels: payload,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.fetchLevelsFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    })),

    // Save rewards
    on(SeasonActions.saveRewardsRequest, (state) => ({
        ...state,
        isLoading: true,
    })),
    on(SeasonActions.saveRewardsSuccess, (state, { payload }) => ({
        ...state,
        rewards: payload,
        isLoading: false,
        error: null,
    })),
    on(SeasonActions.saveRewardsFailure, (state, { payload }) => ({
        ...state,
        isLoading: false,
        error: payload,
    }))
);

export const seasonsReducer = (
    state: SeasonsState | undefined,
    action: Action
) => reducer(state, action);
